/* eslint-disable no-use-before-define */
/* eslint-disable camelcase */
import { FC, useMemo } from 'react';
import {
  IconArticle,
  IconBarrons,
  IconDjRiskJournal,
  IconDowjones,
  IconDragonfly,
  IconFnlondon,
  IconIbd,
  IconInteractive,
  IconMansionglobal,
  IconMarketwatch,
  IconMegaphone,
  IconMic,
  IconOpis,
  IconOxan,
  IconPackage,
  IconPen,
  IconPenta,
  IconShoppingCart,
  IconVideo,
  IconWsj,
  IconWsjopinion,
  IconWsjPro,
  IconZap,
  IconZapFilled,
  SvgIcon,
  Token
} from '@screentone/core';

import {
  IconAfp,
  IconOpinionFreeExpression,
  IconPrivateMarket
} from 'components/datamodel/content/Article/customBrandIcons';
import { PublicationSettingSearchableContentType } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';

interface ContentTypeTokenProps {
  content?: AllessehContent;
  type: PublicationSettingSearchableContentType | string;
  size?: string;
  margin: Record<string, string>;
  color?: string;
  className?: string;
  currentProperty?: string | null;
  iconSvgPath?: string;
}

const getLiveCoverageIcon = (
  content: AllessehContent | undefined
): { icon: React.ComponentType<any>; description: string }[] => {
  if (content) {
    const {
      data: {
        attributes: { section_type }
      }
    } = content;

    if (section_type === 'card') {
      return [{ icon: IconZapFilled, description: 'Live Coverage Card' }];
    }
  }

  return [{ icon: IconZap, description: 'Live Coverage Event' }];
};

export const getContentTypeIcons = (
  type: ContentTypeTokenProps['type'],
  content: ContentTypeTokenProps['content']
): { icon: React.ComponentType<any>; description: string }[] => {
  switch (type.toLowerCase()) {
    case PublicationSettingSearchableContentType.CommerceProduct.toLowerCase():
      return [{ icon: IconShoppingCart, description: 'Commerce Product' }];
    case PublicationSettingSearchableContentType.Article.toLowerCase():
      return [{ icon: IconArticle, description: 'Article' }];
    case PublicationSettingSearchableContentType.Audio.toLowerCase():
      return [{ icon: IconMic, description: 'Audio' }];
    case PublicationSettingSearchableContentType.Video.toLowerCase():
    case PublicationSettingSearchableContentType.WsjOpinionVideo.toLowerCase():
      return [{ icon: IconVideo, description: 'WSJ Opinion Video' }];
    case PublicationSettingSearchableContentType.LiveCoverage.toLowerCase():
      return getLiveCoverageIcon(content);
    case PublicationSettingSearchableContentType.Tappable.toLowerCase():
    case PublicationSettingSearchableContentType.Webstory.toLowerCase():
      return [{ icon: IconInteractive, description: 'Interactive Media' }];
    // linked items are not a searchable content type
    case 'linkeditem':
      return [{ icon: IconPackage, description: 'Linked Item' }];
    case PublicationSettingSearchableContentType.Marketwatch.toLowerCase():
      return [{ icon: IconMarketwatch, description: 'MarketWatch' }];
    case PublicationSettingSearchableContentType.MarketwatchVideo.toLowerCase():
      return [{ icon: IconVideo, description: 'MarketWatch Video' }];
    case PublicationSettingSearchableContentType.MarketwatchLiveCoverageEvents.toLowerCase():
      return [{ icon: IconZap, description: 'MarketWatch Live Coverage Event' }];
    case PublicationSettingSearchableContentType.MarketwatchLiveCoverageCards.toLowerCase():
      return [{ icon: IconZapFilled, description: 'MarketWatch Live Coverage Card' }];
    case PublicationSettingSearchableContentType.Barrons.toLowerCase():
    case PublicationSettingSearchableContentType.BarronsBlog.toLowerCase():
    case PublicationSettingSearchableContentType.BarronsInvestorCircle.toLowerCase():
      return [{ icon: IconBarrons, description: 'Barrons' }];
    case PublicationSettingSearchableContentType.BarronsVideo.toLowerCase():
      return [{ icon: IconVideo, description: 'Barrons Video' }];
    case PublicationSettingSearchableContentType.BarronsLiveCoverageEvents.toLowerCase():
      return [{ icon: IconZap, description: 'Barrons Live Coverage Event' }];
    case PublicationSettingSearchableContentType.BarronsLiveCoverageCards.toLowerCase():
      return [{ icon: IconZapFilled, description: 'Barrons Live Coverage Card' }];
    case PublicationSettingSearchableContentType.Penta.toLowerCase():
      return [{ icon: IconPenta, description: 'Penta' }];
    case PublicationSettingSearchableContentType.MansionGlobal.toLowerCase():
      return [{ icon: IconMansionglobal, description: 'Mansion Global' }];
    case PublicationSettingSearchableContentType.Afp.toLowerCase():
      return [{ icon: IconAfp, description: 'AFP' }];
    case PublicationSettingSearchableContentType.Wsj.toLowerCase():
      return [{ icon: IconWsj, description: 'WSJ' }];
    case PublicationSettingSearchableContentType.WsjVideo.toLowerCase():
      return [{ icon: IconVideo, description: 'WSJ Video' }];
    case PublicationSettingSearchableContentType.WsjLiveCoverageEvents.toLowerCase():
      return [{ icon: IconZap, description: 'WSJ Live Coverage Event' }];
    case PublicationSettingSearchableContentType.WsjLiveCoverageCards.toLowerCase():
      return [{ icon: IconZapFilled, description: 'WSJ Live Coverage Card' }];
    case PublicationSettingSearchableContentType.Opinion.toLowerCase():
      return [{ icon: IconWsjopinion, description: 'WSJ Opinion' }];
    case PublicationSettingSearchableContentType.OxanAnnouncements.toLowerCase():
      return [{ icon: IconMegaphone, description: 'Announcement' }];
    case PublicationSettingSearchableContentType.WsjPro.toLowerCase():
    case PublicationSettingSearchableContentType.WsjProPe.toLowerCase():
      return [{ icon: IconWsjPro, description: 'WSJ Pro' }];
    case PublicationSettingSearchableContentType.PrivateMarketEvents.toLowerCase():
      return [{ icon: IconPrivateMarket, description: 'Private Market Events' }];
    case PublicationSettingSearchableContentType.FreeExpression.toLowerCase():
      return [{ icon: IconOpinionFreeExpression, description: 'Free Expression' }];
    case PublicationSettingSearchableContentType.Penews.toLowerCase():
      return [{ icon: IconPen, description: 'PENews' }];
    case PublicationSettingSearchableContentType.Ibd.toLowerCase():
      return [{ icon: IconIbd, description: 'IBD' }];
    case PublicationSettingSearchableContentType.FnOnline.toLowerCase():
      return [{ icon: IconFnlondon, description: 'FN London' }];
    case PublicationSettingSearchableContentType.OxfordAnalytica.toLowerCase():
      return [{ icon: IconOxan, description: 'Oxford Analytica' }];
    case PublicationSettingSearchableContentType.DowJonesRiskJournal.toLowerCase():
    case PublicationSettingSearchableContentType.DowJonesRiskJournalCcoEvents.toLowerCase():
    case PublicationSettingSearchableContentType.DowJonesRiskJournalContributor.toLowerCase():
    case PublicationSettingSearchableContentType.DowJonesRiskJournalDjEvents.toLowerCase():
    case PublicationSettingSearchableContentType.WorldEcr.toLowerCase():
    case PublicationSettingSearchableContentType.ExportComplianceManager.toLowerCase():
      return [{ icon: IconDjRiskJournal, description: 'Dow Jones Risk Journal' }];
    case PublicationSettingSearchableContentType.Dragonfly.toLowerCase():
      return [{ icon: IconDragonfly, description: 'Dragonfly' }];
    case PublicationSettingSearchableContentType.EnergyNewsEvents.toLowerCase():
      return [{ icon: IconDowjones, description: 'Energy News Events' }];
    case PublicationSettingSearchableContentType.Opis.toLowerCase():
      return [{ icon: IconOpis, description: 'OPIS' }];
    default:
      return [{ icon: IconArticle, description: 'Article' }];
  }
};

const ContentTypeToken: FC<ContentTypeTokenProps> = ({ content, currentProperty, iconSvgPath, ...props }) => {
  const CustomIcon = useMemo(() => {
    if (!iconSvgPath) return null;
    return function CustomSvgIcon(svgProps: React.ComponentProps<typeof SvgIcon>) {
      return (
        <SvgIcon {...svgProps} viewBox="0 0 48 48">
          <path d={iconSvgPath} />
        </SvgIcon>
      );
    };
  }, [iconSvgPath]);

  if (
    props.type.toLowerCase() === PublicationSettingSearchableContentType.LiveCoverageStack.toLowerCase() ||
    props.type.toLowerCase() === PublicationSettingSearchableContentType.LiveCoverageCard.toLowerCase()
  ) {
    return <LiveCoverageToken content={content} {...props} />;
  }

  if (CustomIcon) {
    return <Token icon={CustomIcon} {...props} />;
  }

  const [{ icon, description }] = getContentTypeIcons(props.type, content);

  if (currentProperty?.toLowerCase() === 'wsjopinion' && description === 'WSJ Opinion') {
    return <Token icon={IconArticle} {...props} />;
  }

  return <Token icon={icon} {...props} />;
};

interface LiveCoverageTokenProps extends ContentTypeTokenProps {
  content?: AllessehContent;
}

const LiveCoverageToken: FC<LiveCoverageTokenProps> = ({ content, ...props }) => {
  let isCard = props.type.toLowerCase() === PublicationSettingSearchableContentType.LiveCoverageCard.toLowerCase();

  if (content) {
    const {
      data: {
        attributes: { section_type }
      }
    } = content;

    if (section_type === 'card') {
      isCard = true;
    }
  }

  if (isCard) {
    return (
      <>
        <Token icon={IconZapFilled} {...props} />
      </>
    );
  }

  return <Token icon={IconZap} {...props} />;
};

export default ContentTypeToken;
